import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, CheckCircle2, XCircle, HelpCircle, Loader2, BookOpen, Plus, Sparkles } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import DeleteConfirmDialog from "./DeleteConfirmDialog";
import AddQuestionDialog from "./AddQuestionDialog";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nurseryId: string;
  lessons: { id: string; title: string }[];
  onAutoExam?: () => void;
}

const difficultyColors: Record<string, string> = {
  easy: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  medium: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  hard: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
};

const difficultyLabels: Record<string, string> = { easy: "سهل", medium: "متوسط", hard: "صعب" };
const typeLabels: Record<string, string> = { multiple_choice: "اختيار من متعدد", true_false: "صح وخطأ", essay: "مقالي" };
const typeIcons: Record<string, React.ReactNode> = {
  multiple_choice: <CheckCircle2 className="w-3.5 h-3.5" />,
  true_false: <XCircle className="w-3.5 h-3.5" />,
  essay: <HelpCircle className="w-3.5 h-3.5" />,
};
const stageLabels: Record<string, string> = { kg1: "KG1", kg2: "KG2", grade1: "أولى ابتدائي", grade2: "ثانية ابتدائي", grade3: "ثالثة ابتدائي" };
const subjectLabels: Record<string, string> = { "عربي": "العربية", "رياضيات": "الرياضيات", "انجليزي": "الإنجليزية", "علوم": "العلوم", "اسلامي": "الإسلامية" };

export default function QuestionBankDialog({ open, onOpenChange, nurseryId, lessons, onAutoExam }: Props) {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [diffFilter, setDiffFilter] = useState("all");
  const [lessonFilter, setLessonFilter] = useState("all");
  const [subjectFilter, setSubjectFilter] = useState("all");
  const [stageFilter, setStageFilter] = useState("all");

  const { data: questions, isLoading, refetch } = useQuery({
    queryKey: ["question-bank", nurseryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("questions")
        .select("*")
        .eq("nursery_id", nurseryId)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: open && !!nurseryId,
  });

  const filtered = (questions || []).filter((q: any) => {
    if (search && !q.question_text.includes(search)) return false;
    if (typeFilter !== "all" && q.question_type !== typeFilter) return false;
    if (diffFilter !== "all" && q.difficulty !== diffFilter) return false;
    if (lessonFilter !== "all" && q.lesson_id !== lessonFilter) return false;
    if (subjectFilter !== "all" && q.subject !== subjectFilter) return false;
    if (stageFilter !== "all" && q.stage !== stageFilter) return false;
    return true;
  });

  const lessonMap = new Map(lessons.map((l) => [l.id, l.title]));

  const stats = {
    total: (questions || []).length,
    mc: (questions || []).filter((q: any) => q.question_type === "multiple_choice").length,
    tf: (questions || []).filter((q: any) => q.question_type === "true_false").length,
    essay: (questions || []).filter((q: any) => q.question_type === "essay").length,
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] flex flex-col" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-primary" />
            📚 بنك الأسئلة
          </DialogTitle>
        </DialogHeader>

        {/* Stats & Actions */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex gap-2 text-xs">
            <Badge variant="outline" className="font-bold">{stats.total} سؤال</Badge>
            <Badge variant="secondary">{stats.mc} اختياري</Badge>
            <Badge variant="secondary">{stats.tf} صح/خطأ</Badge>
            <Badge variant="secondary">{stats.essay} مقالي</Badge>
          </div>
          <div className="flex gap-2">
            <AddQuestionDialog
              nurseryId={nurseryId}
              lessons={lessons}
              onSuccess={refetch}
              trigger={
                <Button size="sm" variant="outline">
                  <Plus className="w-4 h-4 ml-1" />
                  إضافة سؤال
                </Button>
              }
            />
            {onAutoExam && (
              <Button size="sm" onClick={onAutoExam}>
                <Sparkles className="w-4 h-4 ml-1" />
                إنشاء امتحان
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          <div className="relative flex-1 min-w-[140px]">
            <Search className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <Input placeholder="بحث..." value={search} onChange={(e) => setSearch(e.target.value)} className="pr-8 h-9 text-sm" />
          </div>
          <Select value={subjectFilter} onValueChange={setSubjectFilter}>
            <SelectTrigger className="w-[130px] h-9 text-sm"><SelectValue placeholder="المادة" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">جميع المواد</SelectItem>
              <SelectItem value="عربي">العربية</SelectItem>
              <SelectItem value="رياضيات">الرياضيات</SelectItem>
              <SelectItem value="انجليزي">الإنجليزية</SelectItem>
              <SelectItem value="علوم">العلوم</SelectItem>
              <SelectItem value="اسلامي">الإسلامية</SelectItem>
            </SelectContent>
          </Select>
          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger className="w-[120px] h-9 text-sm"><SelectValue placeholder="المرحلة" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">الكل</SelectItem>
              <SelectItem value="kg1">KG1</SelectItem>
              <SelectItem value="kg2">KG2</SelectItem>
              <SelectItem value="grade1">أولى ابتدائي</SelectItem>
              <SelectItem value="grade2">ثانية ابتدائي</SelectItem>
              <SelectItem value="grade3">ثالثة ابتدائي</SelectItem>
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[130px] h-9 text-sm"><SelectValue placeholder="النوع" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">جميع الأنواع</SelectItem>
              <SelectItem value="multiple_choice">اختيار من متعدد</SelectItem>
              <SelectItem value="true_false">صح وخطأ</SelectItem>
              <SelectItem value="essay">مقالي</SelectItem>
            </SelectContent>
          </Select>
          <Select value={diffFilter} onValueChange={setDiffFilter}>
            <SelectTrigger className="w-[100px] h-9 text-sm"><SelectValue placeholder="الصعوبة" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">الكل</SelectItem>
              <SelectItem value="easy">سهل</SelectItem>
              <SelectItem value="medium">متوسط</SelectItem>
              <SelectItem value="hard">صعب</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Questions List */}
        <ScrollArea className="flex-1 max-h-[50vh]">
          {isLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-8 space-y-2">
              <div className="text-4xl">📭</div>
              <p className="text-muted-foreground text-sm">لا توجد أسئلة</p>
              <p className="text-xs text-muted-foreground">أضف أسئلة يدوياً أو ولّد أسئلة بالذكاء الاصطناعي من الدروس</p>
            </div>
          ) : (
            <div className="space-y-2 pl-2">
              {filtered.map((q: any, i: number) => (
                <div key={q.id} className="border rounded-lg p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium flex-1">{i + 1}. {q.question_text}</p>
                    <DeleteConfirmDialog
                      title="حذف السؤال"
                      description="هل أنت متأكد من حذف هذا السؤال؟"
                      onConfirm={async () => {
                        const { error } = await supabase.from("questions").delete().eq("id", q.id);
                        if (error) toast({ title: "خطأ", description: error.message, variant: "destructive" });
                        else { toast({ title: "تم حذف السؤال" }); refetch(); }
                      }}
                    />
                  </div>

                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge variant="outline" className="text-xs flex items-center gap-1">
                      {typeIcons[q.question_type]}
                      {typeLabels[q.question_type] || q.question_type}
                    </Badge>
                    <Badge className={`text-xs ${difficultyColors[q.difficulty] || ""}`}>
                      {difficultyLabels[q.difficulty] || q.difficulty}
                    </Badge>
                    {q.subject && (
                      <Badge variant="secondary" className="text-xs">
                        {subjectLabels[q.subject] || q.subject}
                      </Badge>
                    )}
                    {q.stage && (
                      <Badge variant="secondary" className="text-xs">
                        {stageLabels[q.stage] || q.stage}
                      </Badge>
                    )}
                    {q.is_ai_generated && <Badge variant="secondary" className="text-xs">🤖 AI</Badge>}
                    {q.lesson_id && lessonMap.has(q.lesson_id) && (
                      <Badge variant="outline" className="text-xs">{lessonMap.get(q.lesson_id)}</Badge>
                    )}
                  </div>

                  {q.question_type === "multiple_choice" && q.options && (
                    <div className="grid grid-cols-2 gap-1">
                      {(q.options as string[]).map((opt: string, oi: number) => (
                        <div key={oi} className={`text-xs rounded px-2 py-1 border ${
                          opt === q.correct_answer
                            ? "border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400"
                            : "border-border"
                        }`}>
                          {opt === q.correct_answer ? "✓ " : ""}{opt}
                        </div>
                      ))}
                    </div>
                  )}

                  {q.question_type === "true_false" && (
                    <p className="text-xs text-muted-foreground">الإجابة: <span className="font-medium text-foreground">{q.correct_answer === "true" ? "صح ✓" : "خطأ ✗"}</span></p>
                  )}

                  {q.question_type === "essay" && (
                    <p className="text-xs text-muted-foreground">الإجابة: <span className="text-foreground">{q.correct_answer}</span></p>
                  )}
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
